// The signed-out home: what Foco makes, from a pasted link to a video that sells. The link a visitor pastes goes
// with them to sign-up (/empezar), is held through the email confirmation and lands in their first composer.
import { useEffect, useRef, useState, type RefObject } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Link2,
} from "lucide-react";
import type { StudioConfig } from "../shared/types";
import { PLAN_IDS, PLANS, videosFor } from "../shared/catalog";
import { ideaFor, ideas, type CreationIntent } from "../shared/creation";
import { IconButton, LanguageSwitch, Logo, Ring } from "./components";
import { prefersReducedMotion } from "./fx";
import { PromoPhone } from "./Promo";
import { euro, exVat } from "./Plans";
import { bilingual, useLang, useText } from "./i18n";
import { track, watchPage } from "./usage";

const TEXT = bilingual({
  en: {
    nav: { plans: "Pricing", signIn: "Sign in", start: "Make my video" },
    hero: {
      title: ["Paste a link.", "Get a video", "that sells."],
      lede: "Foco reads your product page and builds a vertical video with a hook, voiceover, music and captions. Ready for Reels, TikTok and ads.",
      label: "Your product's link",
      placeholder: "yourstore.com/product",
      photos: "No website? A few photos will do.",
      from: (videos: number, price: string) =>
        `${videos} videos a month from ${price}`,
      cancel: "Cancel anytime",
    },
    examples: {
      title: "From launch day to Friday’s offer.",
      sub: "Pick a starting point, or let Foco decide.",
      note: "Example brands",
      prev: "Previous videos",
      next: "More videos",
    },
    how: {
      title: "You bring the product. Foco makes the video.",
      steps: [
        { title: "Paste the link", text: "Or drop in a few photos. That’s enough." },
        { title: "Foco directs it", text: "It writes the script, picks the voice and music, and cuts every shot." },
        { title: "Download and post", text: "Want a change? Ask in the chat." },
      ],
      stages: ["Script and hook", "Studio voice", "Music", "Edit and captions"],
      file: "Chilliwren · Launch",
      specs: ["1080×1920", "60 fps", "Captions", "10 studio voices", "36 included songs"],
    },
    pricing: {
      title: "Choose how many videos a month.",
      sub: "Every plan has the same studio.",
      recommended: "Recommended",
      videos: "videos a month",
      per: "+ VAT a month",
      first: (n: number) => `${n} in your first month`,
      choose: "Get started",
      fine: "Estimates for videos made in one go with Foco. Change or cancel anytime.",
      details: "Plan details",
    },
    faq: {
      title: "Questions",
      items: [
        {
          q: "Do I need to know video editing?",
          a: "No. Foco writes the script, adds the voice, picks the music and edits the video. You watch it and download it.",
        },
        {
          q: "What if I don’t have a website?",
          a: "Upload a few photos of the product and say what you sell. That’s enough.",
        },
        {
          q: "Can I ask for changes?",
          a: "Yes, in the chat: “open with the price”, “make it shorter”, “different music”. Changes use credits too.",
        },
        {
          q: "What do I get?",
          a: "A vertical 1080×1920 MP4 at 60 fps, ready for Reels, TikTok, Shorts and ads.",
        },
        {
          q: "Can I cancel?",
          a: "Anytime, from Credits. A cancellation takes effect when the month ends.",
        },
      ],
    },
    final: "Your next video starts with a link.",
    footer: { legal: "Terms and privacy" },
  },
  es: {
    nav: { plans: "Precios", signIn: "Entrar", start: "Crear mi vídeo" },
    hero: {
      title: ["Pega un enlace.", "Sale un vídeo", "que vende."],
      lede: "Foco lee la página de tu producto y monta un vídeo vertical con gancho, voz, música y subtítulos. Listo para Reels, TikTok y anuncios.",
      label: "Enlace de tu producto",
      placeholder: "tutienda.com/producto",
      photos: "¿Sin web? Bastan unas fotos.",
      from: (videos: number, price: string) =>
        `${videos} vídeos al mes desde ${price}`,
      cancel: "Cancela cuando quieras",
    },
    examples: {
      title: "Del lanzamiento a la oferta del viernes.",
      sub: "Elige un punto de partida o deja que Foco decida.",
      note: "Marcas de ejemplo",
      prev: "Vídeos anteriores",
      next: "Más vídeos",
    },
    how: {
      title: "Tú pones el producto. Foco, el vídeo.",
      steps: [
        { title: "Pega el enlace", text: "O sube unas fotos. Con eso basta." },
        { title: "Foco lo dirige", text: "Escribe el guion, elige la voz y la música y monta cada plano." },
        { title: "Descárgalo y publícalo", text: "¿Algún cambio? Pídelo en el chat." },
      ],
      stages: ["Guion y gancho", "Voz de estudio", "Música", "Montaje y subtítulos"],
      file: "Chilliwren · Lanzamiento",
      specs: ["1080×1920", "60 fps", "Subtítulos", "10 voces de estudio", "36 canciones incluidas"],
    },
    pricing: {
      title: "Elige cuántos vídeos al mes.",
      sub: "Todos los planes tienen el mismo estudio.",
      recommended: "Recomendado",
      videos: "vídeos al mes",
      per: "+ IVA al mes",
      first: (n: number) => `${n} el primer mes`,
      choose: "Empezar",
      fine: "Estimación para vídeos hechos a la primera con Foco. Cambia o cancela cuando quieras.",
      details: "Detalles de los planes",
    },
    faq: {
      title: "Preguntas",
      items: [
        {
          q: "¿Necesito saber editar vídeo?",
          a: "No. Foco escribe el guion, pone la voz, elige la música y monta el vídeo. Tú lo ves y lo descargas.",
        },
        {
          q: "¿Y si no tengo web?",
          a: "Sube unas fotos del producto y cuéntale qué vendes. Con eso basta.",
        },
        {
          q: "¿Puedo pedir cambios?",
          a: "Sí, en el chat: «que empiece por el precio», «más corto», «otra música». Los cambios también usan créditos.",
        },
        {
          q: "¿Qué me llevo?",
          a: "Un MP4 vertical de 1080×1920 a 60 fps, listo para Reels, TikTok, Shorts y anuncios.",
        },
        {
          q: "¿Puedo cancelar?",
          a: "Cuando quieras, desde Créditos. La cancelación se aplica al terminar el mes.",
        },
      ],
    },
    final: "Tu próximo vídeo empieza con un enlace.",
    footer: { legal: "Términos y privacidad" },
  },
});

/** Finished videos of invented brands (rendered from ~/Documents/foco-reels src/Examples.tsx), by starting point. */
const EXAMPLES: { id: string; idea: CreationIntent; brand: string }[] = [
  { id: "chilli", idea: "intro", brand: "Chilliwren" },
  { id: "invoice", idea: "feature", brand: "Invoicewren" },
  { id: "tread", idea: "offer", brand: "Treadmoss" },
  { id: "billow", idea: "tutorial", brand: "Billowfern" },
  { id: "tour", idea: "tour", brand: "Hushdawn" },
  { id: "roast", idea: "series", brand: "Roastgully" },
  { id: "purl", idea: "intro", brand: "Purlhaven" },
  { id: "parrot", idea: "feature", brand: "Parrotine" },
];

export function Landing({ config }: { config: StudioConfig }) {
  const language = useLang(),
    t = useText(TEXT);
  const navigate = useNavigate();
  const rings = useRef<HTMLCanvasElement>(null),
    heroForm = useRef<HTMLFormElement>(null),
    endForm = useRef<HTMLFormElement>(null);
  const [stuck, setStuck] = useState(false),
    [dock, setDock] = useState(false);
  // usage pings (src/usage.ts) while signed out
  useEffect(() => watchPage("/", language), [language]);
  // the header settles over the page once it scrolls; on a phone, the call to action docks at the bottom between
  // the hero's form and the last one
  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 8);
    onScroll();
    addEventListener("scroll", onScroll, { passive: true });
    const seen = new Map<Element, IntersectionObserverEntry>();
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) seen.set(e.target, e);
      const hero = seen.get(heroForm.current!),
        end = seen.get(endForm.current!);
      setDock(
        !!hero &&
          !hero.isIntersecting &&
          hero.boundingClientRect.top < 0 &&
          !end?.isIntersecting,
      );
    });
    io.observe(heroForm.current!);
    io.observe(endForm.current!);
    return () => {
      removeEventListener("scroll", onScroll);
      io.disconnect();
    };
  }, []);
  const start = (brief = "") => {
    track("form", `landing: ${brief ? "link" : "start"}`);
    window.scrollTo(0, 0);
    navigate(config.signup ? "/empezar" : "/entrar", {
      state: brief ? { brief } : null,
    });
  };
  const starter = PLANS.starter;
  return (
    <div className="lp">
      <header className={`lp-top ${stuck ? "stuck" : ""} ${dock ? "cta" : ""}`}>
        <div className="lp-wrap lp-top-in">
          <Logo />
          <nav className="lp-nav">
            <LanguageSwitch className="lp-lang" />
            <a href="#precios">{t.nav.plans}</a>
            <Link to="/entrar">{t.nav.signIn}</Link>
            <button
              type="button"
              className="button primary lp-top-cta"
              tabIndex={dock ? 0 : -1}
              onClick={() => start()}
            >
              {t.nav.start}
            </button>
          </nav>
        </div>
      </header>

      <section className="lp-hero" aria-labelledby="hero-h">
        <canvas ref={rings} className="lp-rings" aria-hidden="true" />
        <div className="lp-wrap lp-hero-in">
          <div className="lp-copy">
            <h1 id="hero-h" className="lp-title">
              {t.hero.title[0]}
              <br />
              {t.hero.title[1]}
              <br />
              <span className="focus-word">{t.hero.title[2]}</span>
            </h1>
            <p className="lp-lede">{t.hero.lede}</p>
            <LinkForm formRef={heroForm} onStart={start} />
            <button
              type="button"
              className="lp-photos"
              onClick={() => start()}
            >
              {t.hero.photos}
            </button>
            <ul className="lp-facts">
              <li>
                {t.hero.from(
                  videosFor(starter.credits, "foco"),
                  exVat(starter.price),
                )}
              </li>
              <li>{t.hero.cancel}</li>
            </ul>
          </div>
          <div className="lp-stage">
            <PromoPhone rings={rings} />
          </div>
        </div>
      </section>

      <Examples />

      <section className="lp-section" aria-labelledby="how-h">
        <div className="lp-wrap">
          <h2 id="how-h" className="lp-h2">
            {t.how.title}
          </h2>
          <ol className="lp-steps">
            {t.how.steps.map((s, i) => (
              <li className="lp-step" key={s.title}>
                <div className="lp-art" aria-hidden="true">
                  {i === 0 ? <ComposerArt /> : i === 1 ? <DirectorArt /> : <FileArt />}
                </div>
                <span className="lp-n">{String(i + 1).padStart(2, "0")}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
          <ul className="lp-specs">
            {t.how.specs.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="lp-section" id="precios" aria-labelledby="pricing-h">
        <div className="lp-wrap">
          <div className="lp-head">
            <h2 id="pricing-h" className="lp-h2">
              {t.pricing.title}
            </h2>
            <p className="lp-sub">{t.pricing.sub}</p>
          </div>
          <div className="lp-plans">
            {PLAN_IDS.map((id) => {
              const p = PLANS[id],
                featured = id === "pro";
              return (
                <article className={`lp-plan ${featured ? "featured" : ""}`} key={id}>
                  <header>
                    <h3>{p.name}</h3>
                    {featured && <span className="lp-badge">{t.pricing.recommended}</span>}
                  </header>
                  <p className="lp-count">
                    <b>{videosFor(p.credits, "foco")}</b>
                    <span>{t.pricing.videos}</span>
                  </p>
                  <p className="lp-price">
                    <b>{euro(p.price)}</b> {t.pricing.per}
                  </p>
                  <p className="lp-first">
                    {t.pricing.first(videosFor(p.credits + p.welcome, "foco"))}
                  </p>
                  <button
                    type="button"
                    className={`button ${featured ? "light" : "ghost"} large full`}
                    onClick={() => start()}
                  >
                    {t.pricing.choose}
                  </button>
                </article>
              );
            })}
          </div>
          <p className="lp-fine">
            {t.pricing.fine} <Link to="/plans">{t.pricing.details}</Link>
          </p>
        </div>
      </section>

      <section className="lp-section" aria-labelledby="faq-h">
        <div className="lp-wrap lp-narrow">
          <div className="plans-faq">
            <h2 id="faq-h" className="h2">
              {t.faq.title}
            </h2>
            {t.faq.items.map(({ q, a }) => (
              <details key={q}>
                <summary>
                  {q}
                  <ChevronDown size={16} />
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="lp-final" aria-labelledby="start-h">
        <svg className="lp-final-rings" viewBox="0 0 1200 600" aria-hidden="true">
          {[90, 150, 240, 384, 614].map((r) => (
            <circle key={r} cx="600" cy="300" r={r} />
          ))}
        </svg>
        <div className="lp-wrap lp-final-in">
          <h2 id="start-h" className="lp-h2 lp-final-h">
            {t.final}
          </h2>
          <LinkForm formRef={endForm} onStart={start} />
        </div>
      </section>

      <footer className="lp-foot">
        <div className="lp-wrap lp-foot-in">
          <Logo />
          <nav>
            <Link to="/plans">{t.nav.plans}</Link>
            <Link to="/legal">{t.footer.legal}</Link>
            {config.support && (
              <a href={`mailto:${config.support}`}>{config.support}</a>
            )}
          </nav>
          <LanguageSwitch />
        </div>
      </footer>

      <div className={`lp-dock ${dock ? "on" : ""}`} aria-hidden={!dock}>
        <button
          type="button"
          className="button primary large full"
          tabIndex={dock ? 0 : -1}
          onClick={() => start()}
        >
          {t.nav.start}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

/** The product's link, and the way in. Anything typed goes along as the first brief. */
function LinkForm({
  formRef,
  onStart,
}: {
  formRef: RefObject<HTMLFormElement | null>;
  onStart: (brief: string) => void;
}) {
  const t = useText(TEXT);
  const [value, setValue] = useState("");
  return (
    <form
      ref={formRef}
      className="lp-form"
      onSubmit={(e) => {
        e.preventDefault();
        onStart(value.trim());
      }}
    >
      <label className="lp-field">
        <Link2 size={18} />
        <span className="sr-only">{t.hero.label}</span>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={t.hero.placeholder}
          inputMode="url"
          autoComplete="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          maxLength={2000}
        />
      </label>
      <button type="submit" className="button primary large">
        {t.nav.start}
        <ArrowRight size={18} />
      </button>
    </form>
  );
}

function Examples() {
  const language = useLang(),
    t = useText(TEXT).examples;
  const rail = useRef<HTMLDivElement>(null);
  const by = (dir: number) =>
    rail.current?.scrollBy({
      left: dir * rail.current.clientWidth * 0.8,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  return (
    <section className="lp-section lp-examples" aria-labelledby="examples-h">
      <div className="lp-wrap lp-head lp-head-row">
        <div>
          <h2 id="examples-h" className="lp-h2">
            {t.title}
          </h2>
          <p className="lp-sub">{t.sub}</p>
        </div>
        <div className="lp-rail-nav">
          <IconButton label={t.prev} onClick={() => by(-1)}>
            <ChevronLeft size={18} />
          </IconButton>
          <IconButton label={t.next} onClick={() => by(1)}>
            <ChevronRight size={18} />
          </IconButton>
        </div>
      </div>
      <div
        className="lp-rail"
        ref={rail}
        role="region"
        aria-labelledby="examples-h"
        tabIndex={0}
      >
        {EXAMPLES.map((e) => (
          <figure className="lp-ex" key={e.id}>
            <Loop id={e.id} />
            <figcaption>
              <span className="lp-tag">{ideaFor(e.idea, language)?.label}</span>
              <span className="lp-brand">{e.brand}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      <p className="lp-wrap lp-note">{t.note}</p>
    </section>
  );
}

/** An example video, playing only while it's on screen. */
function Loop({ id }: { id: string }) {
  const video = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = video.current!;
    if (prefersReducedMotion()) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) void v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.4 },
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  return (
    <span className="lp-ex-screen">
      <video
        ref={video}
        src={`/examples/${id}.mp4`}
        poster={`/examples/${id}.jpg`}
        muted
        loop
        playsInline
        preload="none"
        aria-hidden="true"
      />
    </span>
  );
}

function ComposerArt() {
  const language = useLang();
  return (
    <div className="lp-mock">
      <div className="lp-mock-field">
        <Link2 size={14} />
        <span>hushdawn.com/lampara</span>
        <i className="lp-caret" />
      </div>
      <div className="lp-mock-chips">
        {ideas(language)
          .slice(0, 4)
          .map((i) => (
            <span key={i.id}>{i.label}</span>
          ))}
      </div>
    </div>
  );
}

function DirectorArt() {
  const t = useText(TEXT).how;
  return (
    <ul className="lp-mock lp-mock-stages">
      {t.stages.map((s, i) => (
        <li key={s} className={i === t.stages.length - 1 ? "live" : ""}>
          {i === t.stages.length - 1 ? <Ring /> : <Check size={14} />}
          {s}
        </li>
      ))}
    </ul>
  );
}

function FileArt() {
  const t = useText(TEXT).how;
  return (
    <div className="lp-mock lp-mock-file">
      <img src="/examples/chilli.jpg" alt="" loading="lazy" />
      <div>
        <b>{t.file}</b>
        <span>1080×1920 · 60 fps</span>
      </div>
      <span className="lp-dl">
        <Download size={15} />
      </span>
    </div>
  );
}
